/**
 * Word Defense game logic.
 * Words approach a target. Typing correctly removes them. One life.
 * Pure functions on a plain state object.
 */

import { makeRng, pickWord, DIFFICULTY_MULTIPLIERS, DIFFICULTY_SETTINGS } from '../random';

const BASE_POINTS = 12;
const COMBO_BONUS_PER_WORD = 3;

/**
 * @typedef {Object} DefenseWord
 * @property {string} id
 * @property {string} text
 * @property {number} spawnedAtMs
 * @property {number} travelMs
 */

/**
 * @typedef {Object} WordDefenseState
 * @property {'running' | 'ended'} phase
 * @property {number} seed
 * @property {'easy'|'medium'|'hard'} difficulty
 * @property {ReturnType<typeof makeRng>} rng
 * @property {DefenseWord[]} words
 * @property {number} score
 * @property {number} combo
 * @property {number} correctTyped
 * @property {number} incorrectTyped
 * @property {number} startedAtMs
 * @property {number} lastSpawnAtMs
 * @property {string} typedBuffer
 * @property {string} endReason
 */

/**
 * @param {{ seed?: number, difficulty?: 'easy'|'medium'|'hard' }} opts
 * @returns {WordDefenseState}
 */
export function createGame({ seed = 1, difficulty = 'medium' } = {}) {
  return {
    phase: 'running',
    seed,
    difficulty,
    rng: makeRng(seed),
    words: [],
    score: 0,
    combo: 0,
    correctTyped: 0,
    incorrectTyped: 0,
    startedAtMs: 0,
    lastSpawnAtMs: 0,
    typedBuffer: '',
    endReason: null,
  };
}

/**
 * @param {WordDefenseState} state
 * @param {number} nowMs
 */
export function tick(state, nowMs) {
  if (state.phase !== 'running') return state;

  let next = { ...state };
  if (next.startedAtMs === 0) {
    next.startedAtMs = nowMs;
    next.lastSpawnAtMs = nowMs;
  }

  const settings = DIFFICULTY_SETTINGS[next.difficulty];
  const ramps = Math.floor((nowMs - next.startedAtMs) / 30_000);
  const spawnInterval = Math.max(500, settings.spawnIntervalMs * 0.9 / (1 + ramps * settings.speedupPer30s));

  if (nowMs - next.lastSpawnAtMs >= spawnInterval) {
    next.words = [
      ...next.words,
      {
        id: `d_${next.words.length}_${Math.floor(nowMs)}`,
        text: pickWord(next.rng),
        spawnedAtMs: nowMs,
        travelMs: settings.fallDurationMs * 1.1,
      },
    ];
    next.lastSpawnAtMs = nowMs;
  }

  // Any word reaching the target ends the game.
  const breached = next.words.some(
    (w) => nowMs - w.spawnedAtMs >= w.travelMs
  );
  if (breached) {
    next.words = next.words.filter((w) => nowMs - w.spawnedAtMs < w.travelMs);
    next.phase = 'ended';
    next.endReason = 'breach';
  }

  return next;
}

/**
 * @param {WordDefenseState} state
 * @param {string} char
 */
export function typeChar(state, char) {
  if (state.phase !== 'running') return state;

  const candidate = state.typedBuffer + char;
  const matches = state.words.filter((w) =>
    w.text.toLowerCase().startsWith(candidate.toLowerCase())
  );

  if (matches.length === 0) {
    return { ...state, typedBuffer: '', incorrectTyped: state.incorrectTyped + 1, combo: 0 };
  }

  const completed = matches.find((w) => w.text.toLowerCase() === candidate.toLowerCase());
  if (completed) {
    const newCombo = state.combo + 1;
    const points = BASE_POINTS + COMBO_BONUS_PER_WORD * newCombo;
    return {
      ...state,
      words: state.words.filter((w) => w.id !== completed.id),
      score: state.score + Math.round(points * DIFFICULTY_MULTIPLIERS[state.difficulty]),
      combo: newCombo,
      correctTyped: state.correctTyped + 1,
      typedBuffer: '',
    };
  }
  return { ...state, typedBuffer: candidate };
}

/**
 * @param {WordDefenseState} state
 * @param {number} nowMs
 */
export function nearestWordProgress(state, nowMs) {
  if (state.words.length === 0) return 0;
  let max = 0;
  for (const w of state.words) {
    const p = (nowMs - w.spawnedAtMs) / w.travelMs;
    if (p > max) max = p;
  }
  return Math.min(1, max);
}